import {
  Injectable,
  inject,
  signal,
  computed,
  effect,
  untracked,
  PLATFORM_ID,
  afterNextRender,
  DestroyRef,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Slide } from '@core/models-v2/cms/slider-reponse.interface';
import { CmsService } from '@core/services-v2/cms.service';
import { GtmService } from '@core/utils-v2/gtm/gtm.service';
import { ScreenService } from '@shared/services/screen';

import { HERO_CAROUSEL_CONFIG_TOKEN } from '../config/hero-carousel.config';

import { HeroCarouselMetaService } from './hero-carousel-meta.service';
import { HeroCarouselPreloadService } from './hero-carousel-proload.service';
import { HeroCarouselState } from '../models/hero-carousel-state.type';
import { distinctUntilChanged } from 'rxjs';

declare let dataLayer: any;

@Injectable()
export class HeroCarouselStateService {
  //#region Dependencies
  private readonly cmsService = inject(CmsService);
  private readonly gtmService = inject(GtmService);
  private readonly screenService = inject(ScreenService);
  private readonly imagePreloader = inject(HeroCarouselPreloadService);
  private readonly metaService = inject(HeroCarouselMetaService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);
  readonly config = inject(HERO_CAROUSEL_CONFIG_TOKEN);
  //#endregion

  //#region State Signals
  private readonly _slides = signal<Slide[]>([]);
  private readonly _carouselReady = signal(false);
  private readonly _hydrated = signal(false);
  private readonly _error = signal<string | null>(null);
  private readonly _retryCount = signal(0);
  //#endregion

  //#region Public Computed State
  readonly slides = this._slides.asReadonly();
  readonly carouselReady = this._carouselReady.asReadonly();
  readonly hydrated = this._hydrated.asReadonly();
  readonly error = this._error.asReadonly();

  readonly displayState = computed((): HeroCarouselState => {
    if (this._error()) return 'error';
    if (!this._slides().length) return 'skeleton';
    //if (!this._carouselReady()) return 'static';
    return 'carousel';
  });

  readonly firstSlide = computed(() => this._slides()[0] ?? null);
  readonly hasSlides = computed(() => this._slides().length > 0);
  readonly isMobile = this.screenService.isMobile;
  //#endregion

  constructor() {
    this.setupHydrationDetection();
    this.setupResourceEffects();

    this.destroyRef.onDestroy(() => {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
    });
  }

  //#region Initialization
  initialize(): void {
    this.loadSlides();
  }

  private setupHydrationDetection(): void {
    afterNextRender(() => {
      this._hydrated.set(true);
      this.scheduleCarouselActivation();
    });
  }

  private setupResourceEffects(): void {
    // Critical resources effect
    // ✅ DEBOUNCE effects para evitar múltiples ejecuciones
    effect(() => {
      const firstSlide = this.firstSlide();
      const isHydrated = this._hydrated();

      if (firstSlide && isHydrated) {
        // ✅ DEBOUNCE para evitar múltiples llamadas
        /*this.debouncePreload(() => {
          this.preloadCriticalResources(firstSlide);
          this.updateMetaTags(firstSlide);
        });*/
        untracked(() => {
          this.preloadCriticalResources(firstSlide);
          this.updateMetaTags(firstSlide);
        });
      }
    });

    // Secondary resources effect
    effect(() => {
      const slides = this._slides();
      const isCarouselReady = this._carouselReady();

      if (slides.length > 1 && isCarouselReady) {
        untracked(() => {
          setTimeout(() => {
            this.preloadSecondaryResources(slides);
          }, this.config.performance.preloadSecondaryDelay);
        });
      }
    });
  }
  //#endregion

  private debounceTimer?: ReturnType<typeof setTimeout>;
  private debouncePreload(fn: () => void): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      untracked(() => fn());
    }, 50);
  }

  //#region Data Management
  private loadSlides(): void {
    this.cmsService
      .getSlides()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        )
      )

      .subscribe({
        next: (slides) => {
          console.log('📥 Setting slides:', slides.length); // ✅ Debug
          this._slides.set(slides);
          this._error.set(null);
          this._retryCount.set(0);
        },
        error: (error) => this.handleLoadError(error),
      });
  }

  private handleLoadError(error: any): void {
    const currentRetry = this._retryCount();

    if (currentRetry < this.config.error.maxRetryAttempts) {
      this._retryCount.set(currentRetry + 1);
      setTimeout(() => {
        this.loadSlides();
      }, Math.pow(2, currentRetry) * 1000);
    } else {
      this._error.set('Failed to load carousel content');
      console.error('Carousel loading failed after retries:', error);
    }
  }

  retryLoad(): void {
    this._error.set(null);
    this._retryCount.set(0);
    this.loadSlides();
  }
  //#endregion

  //#region Resource Management
  private preloadCriticalResources(slide: Slide): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const imageUrl = this.getCurrentSlideImage(slide);
    this.imagePreloader.preloadCritical(imageUrl);
  }

  private preloadSecondaryResources(slides: Slide[]): void {
    if (!isPlatformBrowser(this.platformId) || slides.length < 2) return;

    slides.slice(1, 3).forEach((slide) => {
      const imageUrl = this.getCurrentSlideImage(slide);
      this.imagePreloader.preloadSecondary(imageUrl);
    });
  }

  private updateMetaTags(slide: Slide): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const imageUrl = this.getCurrentSlideImage(slide);
    this.metaService.updateCarouselMeta(slide, imageUrl);
  }
  //#endregion

  //#region Carousel Management
  private scheduleCarouselActivation(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    if ('requestIdleCallback' in window) {
      requestIdleCallback(
        () => {
          this._carouselReady.set(true);
        },
        { timeout: this.config.performance.idleTimeout }
      );
    } else {
      setTimeout(() => {
        this._carouselReady.set(true);
      }, 100);
    }
  }
  //#endregion

  //#region Public Methods
  getCurrentSlideImage(slide: Slide): string {
    // ✅ Usar tu ScreenService para determinar la imagen
    if (this.screenService.isMobile()) {
      return slide.imageMobile;
    }

    if (this.screenService.isTablet()) {
      return /*slide.imageTablet ||*/ slide.imageClassic;
    }

    // Desktop, Large, XLarge
    return slide.imageClassic;
  }

  trackSlideClick(slide: Slide, index: number, event: Event): void {
    event.stopPropagation();

    try {
      this.gtmService.click({
        dataLayer,
        textId: String(index + 1),
        prefix: 'banner',
      });
    } catch (error) {
      console.warn('GTM tracking failed:', error);
    }
  }

  getStructuredData(): string {
    const slides = this._slides();
    if (!slides.length) return '';

    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Productos destacados - Implementos',
      description: 'Repuestos para Camiones, Buses y Remolque',
      url: 'https://implementos.cl',
      numberOfItems: slides.length,
      itemListElement: slides.map((slide, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Product',
          name: slide.alt,
          description: slide.title || slide.alt,
          url: slide.url,
          image: {
            '@type': 'ImageObject',
            url: this.getCurrentSlideImage(slide),
            width: this.isMobile() ? '800' : '1920',
            height: this.isMobile() ? '800' : '576',
          },
        },
      })),
    };

    return JSON.stringify(structuredData);
  }
  //#endregion

  getDebugInfo() {
    return {
      screenInfo: this.screenService.screenInfo(),
      currentBreakpoint: this.screenService.currentBreakpoint(),
      responsiveConfig: this.screenService.responsiveConfig(),
    };
  }
}
